'use strict';
import moment from 'moment';
import { PROCEDURE_SIDE_MAPPING } from '../constants/constantStrings';

export class FollowUpHelpers {
  
  /**
   * Formats procedure description from procedures array
   * @param {Array} procedures - Array of procedure objects
   * @returns {string} Formatted procedure description
   */
  static formatProcedureDescription(procedures) {
    if (!procedures || procedures.length === 0) {
      return 'Unknown Procedure';
    }

    return procedures.map(procedure => {
      const sideMapping = PROCEDURE_SIDE_MAPPING.find(mapping => mapping.code === procedure.Side);
      const sideName = sideMapping ? sideMapping.name : 'Unknown';
      return `${sideName} ${procedure.Code}`;
    }).join(', ');
  }

  /**
   * Transforms study data for associated exams dropdown
   * @param {Array} studies - Array of study objects
   * @returns {Array} Transformed exam data for dropdown
   */
  static transformStudiesToExams(studies) {
    if (!studies || studies.length === 0) {
      return [];
    }

    return studies.map(study => {
      const procedureDescription = this.formatProcedureDescription(study.Procedures);
      
      return {
        Key: study.SDate,
        Value: `${moment(study.StudyDate).format('MM/DD/YYYY')} - Acc. ${study.Accession} - ${procedureDescription}`,
        StudyDate: study.StudyDate,
        SDate: study.SDate,
        Accession: study.Accession,
        ProcedureDescription: procedureDescription
      };
    });
  }

  /**
   * Calculates recall date based on study date and special condition
   * @param {Date} studyDate - The study date
   * @param {string} specialCondition - The recommendation's special condition
   * @returns {Date|null} Calculated recall date or null
   */
  static calculateRecallDate(studyDate, specialCondition) {
    if (!studyDate || !specialCondition) {
      return null;
    }

    const baseDate = new Date(studyDate);
    
    if (specialCondition.includes('+')) {
      // + = 12 months
      baseDate.setFullYear(baseDate.getFullYear() + 1);
      return baseDate;
    } else if (specialCondition.includes('-')) {
      // - = 6 months
      baseDate.setMonth(baseDate.getMonth() + 6);
      return baseDate;
    } else if (specialCondition.includes('*') || specialCondition.includes('!')) {
      // * and ! = 1 day
      baseDate.setDate(baseDate.getDate() + 1);
      return baseDate;
    }
    
    // blank/no interval = no automatic recall date
    return null;
  }

  /**
   * Orders exams by study date (newest first)
   * @param {Array} exams - Array of exam objects
   * @returns {Array} Sorted exams
   */
  static sortExamsByDate(exams) {
    return exams.sort((a, b) => new Date(b.StudyDate) - new Date(a.StudyDate));
  }

  /**
   * Finds exam by SDate key
   * @param {Array} exams - Array of exam objects
   * @param {string} sDate - The SDate to find
   * @returns {Object|null} Found exam or null
   */
  static findExamBySDate(exams, sDate) {
    return exams.find(exam => exam.Key === sDate) || null;
  }

  /**
   * Finds recommendation by code
   * @param {Array} recommendations - Array of recommendation objects
   * @param {string} code - The recommendation code
   * @returns {Object|null} Found recommendation or null
   */
  static findRecommendationByCode(recommendations, code) {
    return recommendations.find(rec => rec.Code === code) || null;
  }

  /**
   * Activates UI-Select dropdown programmatically
   * @param {string} selector - CSS selector for the dropdown
   * @returns {boolean} True if successfully activated, false otherwise
   */
  static activateDropdown(selector) {
    try {
      const element = document.querySelector(selector);
      if (!element) {
        return false;
      }

      const scope = angular.element(element).scope();
      if (scope && scope.$select) {
        scope.$select.activate();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to activate dropdown:', error);
      return false;
    }
  }
} 


// WEBPACK FOOTER //
// ./src/scripts/helpers/followUpHelpers.js